import React, { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { cn } from "../utils/cn";
import { usePlans } from "../hooks/usePlans";
import { useFeatures } from "../hooks/useFeatures";
import type { PricingPlan, FeatureItem } from "../types/plan";
import "./Pricing.css";

type BillingPeriod = "monthly" | "annual";

interface FaqItem {
  question: string;
  answer: string;
}

const faqItems: FaqItem[] = [
  {
    question: "What's the difference between Basic and Advanced?",
    answer:
      "Basic includes all core planning features. Advanced adds customizable market assumptions, investment tracking, goal-based scenarios, and shareable summaries for advisors or family.",
  },
  {
    question: "Can I change plans?",
    answer:
      "Yes, you can upgrade or downgrade at any time. Changes take effect at the start of your next billing cycle.",
  },
  {
    question: "What happens after the trial?",
    answer:
      "After your 7-day free trial, choose a plan to continue with full access. Without a paid plan, some features will be restricted.",
  },
];

/**
 * Format cents to currency string
 * Example: 899 → "$8.99"
 */
function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Pricing — Subscription tier comparison page.
 * Route: /pricing — accessible before and after sign-in.
 * Plans are loaded from Firestore with real-time updates.
 */
export const Pricing: React.FC = () => {
  const [billing, setBilling] = useState<BillingPeriod>("monthly");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  // Real-time Firestore listeners
  const {
    plans: allPlans,
    loading: plansLoading,
    error: plansError,
  } = usePlans();
  const {
    features: allFeatures,
    loading: featuresLoading,
    error: featuresError,
  } = useFeatures();

  // Transform Firestore plans to UI-ready pricing plans
  const pricingPlans = useMemo<PricingPlan[]>(() => {
    if (
      plansLoading ||
      featuresLoading ||
      allPlans.length === 0 ||
      allFeatures.length === 0
    ) {
      return [];
    }

    return allPlans.map((plan) => {
      const planFeatureIds = new Set(plan.features);

      // Build feature list with all features, marking included status
      const features: FeatureItem[] = allFeatures.map((feature) => ({
        id: feature.id,
        name: feature.name,
        included: planFeatureIds.has(feature.id),
      }));

      return {
        id: plan.id,
        name: plan.name,
        description: plan.description,
        category: plan.category,
        monthlyPrice: formatCents(plan.pricing.monthly.amount),
        annualPrice: formatCents(plan.pricing.annual.amount),
        annualRegularPrice: formatCents(plan.pricing.regularAnnual.amount),
        annualMonthly: formatCents(plan.pricing.monthlyEquivalent.amount),
        features,
        recommended: plan.category === "advanced",
        trial: plan.trial,
      };
    });
  }, [allPlans, allFeatures, plansLoading, featuresLoading]);

  const isLoading = plansLoading || featuresLoading;
  const error = plansError || featuresError;

  if (isLoading) {
    return (
      <div className="pricing-page" id="pricing-page">
        <header className="pricing-header" id="pricing-header">
          <Link
            to="/signin"
            className="pricing-logo"
            aria-label="Go to sign in"
          >
            RetireEZ
          </Link>
        </header>
        <main className="pricing-content">
          <p style={{ textAlign: "center", padding: "2rem" }}>
            Loading pricing plans...
          </p>
        </main>
      </div>
    );
  }

  if (error || pricingPlans.length === 0) {
    return (
      <div className="pricing-page" id="pricing-page">
        <header className="pricing-header" id="pricing-header">
          <Link
            to="/signin"
            className="pricing-logo"
            aria-label="Go to sign in"
          >
            RetireEZ
          </Link>
        </header>
        <main className="pricing-content">
          <p style={{ textAlign: "center", padding: "2rem", color: "red" }}>
            {error?.message || "No pricing plans available"}
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="pricing-page" id="pricing-page">
      {/* Header */}
      <header className="pricing-header" id="pricing-header">
        <Link to="/signin" className="pricing-logo" aria-label="Go to sign in">
          RetireEZ
        </Link>
      </header>

      {/* Trial banner */}
      <div className="pricing-trial-banner" id="pricing-trial-banner">
        <span className="pricing-trial-icon" aria-hidden="true">
          ✓
        </span>
        <span>
          Get the first 7 days free. No credit card required. Cancel anytime.
        </span>
      </div>

      {/* Main content */}
      <main className="pricing-content">
        <h1 className="pricing-title">Simple, Transparent Pricing</h1>
        <p className="pricing-subtitle">
          Everything you need for Canadian retirement planning. Try free for 7
          days.
        </p>

        {/* Billing toggle */}
        <div
          className="pricing-billing-toggle"
          id="pricing-billing-toggle"
          role="radiogroup"
          aria-label="Billing period"
        >
          <button
            type="button"
            className={cn(
              "pricing-billing-btn",
              billing === "monthly" && "pricing-billing-btn-active"
            )}
            role="radio"
            aria-checked={billing === "monthly"}
            onClick={() => setBilling("monthly")}
          >
            Monthly
          </button>
          <button
            type="button"
            className={cn(
              "pricing-billing-btn",
              billing === "annual" && "pricing-billing-btn-active"
            )}
            role="radio"
            aria-checked={billing === "annual"}
            onClick={() => setBilling("annual")}
          >
            Annual
            <span className="pricing-discount-badge">Save 24%</span>
          </button>
        </div>

        {/* Plan cards */}
        <div className="pricing-plans" id="pricing-plans">
          {pricingPlans.map((plan) => (
            <div
              key={plan.name}
              className={cn(
                "pricing-plan-card",
                plan.recommended && "pricing-plan-card-recommended"
              )}
              id={`pricing-plan-${plan.category}`}
            >
              {plan.recommended && (
                <div className="pricing-recommended-badge">Recommended</div>
              )}
              <h2 className="pricing-plan-name">{plan.name}</h2>
              <div className="pricing-plan-price">
                <span className="pricing-plan-amount">
                  {billing === "monthly" ? plan.monthlyPrice : plan.annualPrice}
                </span>
                <span className="pricing-plan-period">
                  {billing === "monthly" ? "/mo" : "/yr"}
                </span>
              </div>
              {billing === "annual" && (
                <>
                  <p className="pricing-plan-struck-price">
                    <span className="pricing-plan-struck-amount">
                      {plan.annualRegularPrice}
                    </span>
                  </p>
                  <p className="pricing-plan-monthly-equiv">
                    {plan.annualMonthly}/mo billed annually
                  </p>
                </>
              )}
              <p className="pricing-plan-description">{plan.description}</p>

              <ul className="pricing-plan-features" role="list">
                {plan.features.map((feature) => (
                  <li
                    key={feature.id}
                    className={cn(
                      "pricing-plan-feature",
                      !feature.included && "pricing-plan-feature-disabled"
                    )}
                  >
                    <span className="pricing-feature-icon" aria-hidden="true">
                      {feature.included ? "✓" : "—"}
                    </span>
                    {feature.name}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className={cn(
                  "pricing-plan-cta",
                  plan.recommended && "pricing-plan-cta-primary"
                )}
                onClick={() => navigate("/signin")}
              >
                Start Free Trial
              </button>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <section className="pricing-faq" id="pricing-faq">
          <h2 className="pricing-faq-title">Frequently Asked Questions</h2>
          <div className="pricing-faq-list">
            {faqItems.map((item, i) => (
              <div key={i} className="pricing-faq-item" id={`pricing-faq-${i}`}>
                <button
                  type="button"
                  className="pricing-faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`pricing-faq-answer-${i}`}
                >
                  {item.question}
                  <span className="pricing-faq-chevron" aria-hidden="true">
                    {openFaq === i ? "−" : "+"}
                  </span>
                </button>
                {openFaq === i && (
                  <div
                    id={`pricing-faq-answer-${i}`}
                    className="pricing-faq-answer"
                    role="region"
                  >
                    <p>{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="pricing-footer" id="pricing-footer">
        <p>
          <Link to="/terms" className="pricing-footer-link">
            Terms of Service
          </Link>{" "}
          ·{" "}
          <Link to="/privacy" className="pricing-footer-link">
            Privacy Policy
          </Link>{" "}
          ·{" "}
          <Link to="/signin" className="pricing-footer-link">
            Sign In
          </Link>
        </p>
      </footer>
    </div>
  );
};

export default Pricing;
